import {
    Box,
    Button,
    Card,
    CardContent,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Grid,
    Typography,
} from "@mui/material";
import Logger from "../../../services/Logger";
import React, { useState } from "react";
import UtilService from "../../../services/UtilService";
import AccountService from "../AccountService";

export default function PlayStatistics({ playStats, userId, onReset, title = "Play Statistics" }) {
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [resetting, setResetting] = useState(false);

    if (!playStats) {
        return null;
    }

    const countPlay = playStats.countPlay || 0;
    const countFinished = playStats.countFinished || 0;
    const countIncompleted = countPlay - countFinished;
    const completionPercentage = countPlay > 0 ? Math.round((countFinished / countPlay) * 100) : 0;

    const handleResetClick = () => {
        setConfirmOpen(true);
    };

    const handleConfirmReset = async () => {
        try {
            setResetting(true);
            await AccountService.resetPlayStatistics(userId);
            setConfirmOpen(false);
            if (onReset) {
                onReset();
            }
        } catch (error) {
            Logger.error("Error resetting play statistics:", error);
            alert("Failed to reset play statistics. Please try again.");
        } finally {
            setResetting(false);
        }
    };

    const handleCancelReset = () => {
        setConfirmOpen(false);
    };

    return (
        <>
            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                        <Typography variant="h5">
                            {title}
                        </Typography>
                        <Button
                            variant="outlined"
                            color="error"
                            onClick={handleResetClick}
                            disabled={resetting}
                        >
                            Reset
                        </Button>
                    </Box>
                    <Grid container spacing={2}>
                        <Grid size={{ xs: 12 }}>
                            <Box
                                sx={{
                                    p: 2,
                                    bgcolor: "background.paper",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 3,
                                    flexWrap: "wrap",
                                }}
                            >
                            <Box>
                                <Typography variant="body2" color="text.secondary">
                                    Total Plays
                                </Typography>
                                <Typography variant="h6" fontWeight="bold">
                                    {countPlay}
                                </Typography>
                            </Box>
                            <Box>
                                <Typography variant="body2" color="text.secondary">
                                    Completed
                                </Typography>
                                <Typography variant="h6" fontWeight="bold" color="success.main">
                                    {countFinished}
                                </Typography>
                            </Box>
                            <Box>
                                <Typography variant="body2" color="text.secondary">
                                    Incompleted
                                </Typography>
                                <Typography variant="h6" fontWeight="bold" color="error.main">
                                    {countIncompleted}
                                </Typography>
                            </Box>
                            <Box>
                                <Typography variant="body2" color="text.secondary">
                                    Progress
                                </Typography>
                                <Typography variant="h5" color="primary" fontWeight="bold">
                                    {countFinished}/{countPlay}
                                </Typography>
                            </Box>
                            <Box>
                                <Typography variant="body2" color="text.secondary">
                                    Completion Rate
                                </Typography>
                                <Typography variant="h6" fontWeight="bold">
                                    {completionPercentage}%
                                </Typography>
                            </Box>
                        </Box>
                    </Grid>
                    {playStats.latest && (
                        <>
                            <Grid size={{ xs: 12 }}>
                                <Typography variant="subtitle1" color="text.secondary" sx={{ mt: 2 }}>
                                    Latest Play
                                </Typography>
                            </Grid>
                            <Grid size={{ xs: 12 }}>
                                <Typography variant="body2" color="text.secondary">
                                    Puzzle Title
                                </Typography>
                                <Typography variant="body1">
                                    {playStats.latest?.hidden?.title || "N/A"}
                                </Typography>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <Typography variant="body2" color="text.secondary">
                                    Started At
                                </Typography>
                                <Typography variant="body1">
                                    {playStats.latest?.startedAt
                                        ? UtilService.formatDate(playStats.latest.startedAt) || "N/A"
                                        : "N/A"}
                                </Typography>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <Typography variant="body2" color="text.secondary">
                                    Time Spent
                                </Typography>
                                <Typography variant="body1">
                                    {UtilService.formatCompletionTime(
                                        playStats.latest?.startedAt,
                                        playStats.latest?.finishedAt
                                    ) || "N/A"}
                                </Typography>
                            </Grid>
                        </>
                    )}
                </Grid>
            </CardContent>
        </Card>

        <Dialog open={confirmOpen} onClose={handleCancelReset}>
            <DialogTitle>Confirm Reset</DialogTitle>
            <DialogContent>
                <DialogContentText>
                    Are you sure you want to reset web grid play statistics for this user?
                    This deletes hidden-message / word-search play history and cannot be undone.
                </DialogContentText>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleCancelReset} disabled={resetting}>
                    Cancel
                </Button>
                <Button onClick={handleConfirmReset} color="error" variant="contained" disabled={resetting}>
                    {resetting ? "Resetting..." : "Confirm Reset"}
                </Button>
            </DialogActions>
        </Dialog>
    </>
    );
}

